'use client';

/**
 * UI-004 — the alert layer: toasts and other non-modal notices that must stay visible
 * (and live) while a modal is open.
 *
 * Portals its children into a <body> child marked `data-overlay-layer="alert"`, which
 * useModalLayer never makes inert. Give the notice itself `fixed … z-alert` (300, above
 * z-modal 200). A toast left in the page tree would sit under an open modal's scrim and
 * inside the inert app root — the reason every page toast moves here (UI-004 D6).
 *
 * Renders nothing until mounted, so it is safe in server-rendered trees and never takes
 * part in hydration.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { OVERLAY_LAYER_ATTR } from './useModalLayer';

export default function AlertLayer({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.setAttribute(OVERLAY_LAYER_ATTR, 'alert');
    document.body.appendChild(el);
    setHost(el);
    return () => {
      setHost(null);
      el.remove();
    };
  }, []);

  return host ? createPortal(children, host) : null;
}
